import React, { useState } from 'react';
import { RCard } from './RCard';
import { RModal } from './RModal';
import { RStack } from './RStack';
import { RText } from './RText';
import { RToast } from './RToast';
import { RDocumentUploadForm, DEFAULT_ACCEPTED_FILE_TYPES, DEFAULT_MAX_FILE_SIZE_MB } from './RDocumentUploadForm';

interface RDocumentUploadProps {
    documentTypes: string | string[];
    button: React.ReactElement;
    onSubmit: (file: File, typeName: string) => Promise<void>;
    successMessage?: string;
    errorMessage?: string;
    acceptedFileTypes?: string;
    maxFileSizeMB?: number;
    selectTitle?: string;
    uploadTitle?: string;
    description?: string;
}

export function RDocumentUpload({
    documentTypes,
    button,
    onSubmit,
    successMessage = 'Document uploaded',
    errorMessage = 'Could not upload document',
    acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
    maxFileSizeMB = DEFAULT_MAX_FILE_SIZE_MB,
    selectTitle = 'Select Document Type',
    uploadTitle,
    description,
}: RDocumentUploadProps) {
    const types = Array.isArray(documentTypes) ? documentTypes : [documentTypes];
    const isSingle = !Array.isArray(documentTypes);
    const [open, setOpen] = useState(false);
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [toast, setToast] = useState<string | null>(null);

    function showToast(message: string) {
        setToast(message);
        setTimeout(() => setToast(null), 3000);
    }

    function openModal() {
        setOpen(true);
        setSelectedType(isSingle ? types[0] : null);
    }

    function closeModal() {
        setOpen(false);
        setSelectedType(null);
    }

    async function handleSubmit(file: File): Promise<boolean> {
        if (!selectedType) return false;
        try {
            await onSubmit(file, selectedType);
        } catch {
            showToast(errorMessage);
            return false;
        }
        closeModal();
        showToast(successMessage);
        return true;
    }

    const trigger = React.cloneElement(button, {
        onClick: (e: React.MouseEvent) => {
            button.props.onClick?.(e);
            openModal();
        },
    });

    return (
        <>
            {trigger}
            {open && !selectedType && (
                <RModal title={selectTitle} onClose={closeModal} width={520}>
                    <RStack gap="sm">
                        {types.map((type) => (
                            <RCard key={type} padding="xs" onClick={() => setSelectedType(type)}>
                                <RText weight="semibold">{type}</RText>
                            </RCard>
                        ))}
                    </RStack>
                </RModal>
            )}
            {open && selectedType && (
                <RModal title={uploadTitle ?? `Upload ${selectedType}`} onClose={closeModal} width={560}>
                    <RDocumentUploadForm
                        selectedType={selectedType}
                        onChangeType={isSingle ? null : () => setSelectedType(null)}
                        onCancel={closeModal}
                        onSubmit={handleSubmit}
                        acceptedFileTypes={acceptedFileTypes}
                        maxFileSizeMB={maxFileSizeMB}
                        description={description}
                    />
                </RModal>
            )}
            <RToast message={toast} />
        </>
    );
}
